/**
 * Imprint Customization JavaScript
 * Handles Previously Used Design and New Text Only customizations
 * Compatible with Dawn 15.3 theme
 * Variant-aware: shows imprint UI only when CNL variant is selected (SKU ends with 'CNL')
 */

class ImprintCustomization {
  constructor() {
    this.variantChangeUnsubscriber = undefined;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Find all imprint sections - support both variant-aware and legacy (imprint-product)
    const imprintSections = document.querySelectorAll('.imprint-customization');

    imprintSections.forEach((section) => {
      this.initializeSection(section);
    });

    // Subscribe to variant changes for variant-aware imprint (amalgamated product page)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
      );
    }

    // Initial variant check for variant-aware containers (handles URL variant param, etc.)
    this.runInitialVariantCheck();
  }

  runInitialVariantCheck() {
    const containers = document.querySelectorAll('.imprint-customization[data-imprint-container]');
    if (containers.length === 0) return;

    containers.forEach((container) => {
      const productInfo = container.closest('product-info');
      if (!productInfo) return;
      const variantSelects = productInfo.querySelector('variant-selects [data-selected-variant]');
      if (!variantSelects?.innerHTML) return;
      try {
        const variant = JSON.parse(variantSelects.innerHTML);
        this.toggleImprintContainerForVariant(container, variant);
      } catch (e) {
        // Ignore parse errors
      }
    });
  }

  handleVariantChange(event) {
    const { variant } = event?.data || {};
    if (!variant) return;

    const containers = document.querySelectorAll('.imprint-customization[data-imprint-container]');
    containers.forEach((container) => {
      this.toggleImprintContainerForVariant(container, variant);
    });
  }

  toggleImprintContainerForVariant(container, variant) {
    const isCnl = variant.sku && String(variant.sku).toUpperCase().endsWith('CNL');

    if (isCnl) {
      container.style.display = '';
      this.enableImprintInputs(container);
    } else {
      container.style.display = 'none';
      this.resetImprintFormState(container);
      this.disableImprintInputs(container);
    }
  }

  disableImprintInputs(container) {
    const sectionId = this.getSectionId(container);
    const select = container.querySelector(`#imprint-selection-${sectionId}`);
    const line1Input = container.querySelector(`#imprint-line-1-${sectionId}`);
    const line2Input = container.querySelector(`#imprint-line-2-${sectionId}`);
    if (select) select.setAttribute('disabled', 'disabled');
    if (line1Input) line1Input.setAttribute('disabled', 'disabled');
    if (line2Input) line2Input.setAttribute('disabled', 'disabled');
  }

  enableImprintInputs(container) {
    const sectionId = this.getSectionId(container);
    const select = container.querySelector(`#imprint-selection-${sectionId}`);
    const line1Input = container.querySelector(`#imprint-line-1-${sectionId}`);
    const line2Input = container.querySelector(`#imprint-line-2-${sectionId}`);
    if (select) select.removeAttribute('disabled');
    if (line1Input) line1Input.removeAttribute('disabled');
    if (line2Input) line2Input.removeAttribute('disabled');
  }

  resetImprintFormState(container) {
    const sectionId = this.getSectionId(container);
    const select = container.querySelector(`#imprint-selection-${sectionId}`);
    const line1Input = container.querySelector(`#imprint-line-1-${sectionId}`);
    const line2Input = container.querySelector(`#imprint-line-2-${sectionId}`);
    const textFields = container.querySelector(`#imprint-text-fields-${sectionId}`);

    if (select) select.value = 'Previously Used Design';
    if (textFields) textFields.style.display = 'none';
    if (line1Input) {
      line1Input.value = '';
      line1Input.removeAttribute('required');
    }
    if (line2Input) {
      line2Input.value = '';
      line2Input.removeAttribute('required');
    }
    const count1 = container.querySelector(`#char-count-1-${sectionId}`);
    const count2 = container.querySelector(`#char-count-2-${sectionId}`);
    if (count1) count1.textContent = '0';
    if (count2) count2.textContent = '0';
  }

  initializeSection(section) {
    const sectionId = this.getSectionId(section);
    const select = section.querySelector(`#imprint-selection-${sectionId}`);
    const textFields = section.querySelector(`#imprint-text-fields-${sectionId}`);
    const line1Input = section.querySelector(`#imprint-line-1-${sectionId}`);
    const line2Input = section.querySelector(`#imprint-line-2-${sectionId}`);
    const charCount1 = section.querySelector(`#char-count-1-${sectionId}`);
    const charCount2 = section.querySelector(`#char-count-2-${sectionId}`);

    if (!select || !textFields) return;

    // Initialize dropdown handler
    select.addEventListener('change', () => {
      this.handleSelectionChange(select, textFields, line1Input, line2Input);
    });

    // Initialize input validation and character counting
    if (line1Input && charCount1) {
      this.setupInputValidation(line1Input, charCount1);
    }
    
    if (line2Input && charCount2) {
      this.setupInputValidation(line2Input, charCount2);
    }

    // Set initial state
    this.handleSelectionChange(select, textFields, line1Input, line2Input);
  }

  getSectionId(section) {
    // Extract section ID from the form or use a fallback
    const form = section.closest('product-info');
    if (form && form.id) {
      return form.id.replace('MainProduct-', '');
    }
    return 'default';
  }

  handleSelectionChange(select, textFields, line1Input, line2Input) {
    const selectedValue = select.value;
    
    if (selectedValue === 'New Imprint: Text Only') {
      // Show text fields
      textFields.style.display = 'block';
      
      // Set required attributes
      if (line1Input) line1Input.setAttribute('required', 'required');
      if (line2Input) line2Input.setAttribute('required', 'required');
    } else {
      // Hide text fields
      textFields.style.display = 'none';
      
      // Remove required attributes and clear values
      if (line1Input) {
        line1Input.removeAttribute('required');
        line1Input.value = '';
      }
      if (line2Input) {
        line2Input.removeAttribute('required');
        line2Input.value = '';
      }
    }
  }

  setupInputValidation(input, charCountElement) {
    // Real-time character counting and validation
    const updateCharCount = () => {
      const currentLength = input.value.length;
      charCountElement.textContent = currentLength;
      
      // Add visual feedback for character limit
      if (currentLength >= 30) {
        charCountElement.style.color = '#d73502'; // Dawn's error color
      } else if (currentLength >= 25) {
        charCountElement.style.color = '#b45309'; // Dawn's warning color
      } else {
        charCountElement.style.color = ''; // Reset to default
      }
    };

    // Restrict invalid characters (comma and caret)
    const restrictInput = (event) => {
      const value = event.target.value;
      const restrictedChars = /[,^]/g;
      
      if (restrictedChars.test(value)) {
        event.target.value = value.replace(restrictedChars, '');
        this.showInputError(event.target, 'Comma (,) and caret (^) characters are not allowed');
      } else {
        this.clearInputError(event.target);
      }
      
      updateCharCount();
    };

    // Event listeners
    input.addEventListener('input', restrictInput);
    input.addEventListener('paste', (event) => {
      // Handle paste events with a slight delay to catch the pasted content
      setTimeout(() => restrictInput(event), 10);
    });

    // Initialize character count
    updateCharCount();
  }

  showInputError(input, message) {
    // Remove existing error
    this.clearInputError(input);
    
    // Add error styling
    input.classList.add('field__input--error');
    
    // Create and show error message
    const errorElement = document.createElement('div');
    errorElement.className = 'field__message field__message--error';
    errorElement.textContent = message;
    errorElement.setAttribute('data-imprint-error', 'true');
    
    input.parentNode.appendChild(errorElement);
    
    // Auto-hide error after 3 seconds
    setTimeout(() => {
      this.clearInputError(input);
    }, 3000);
  }

  clearInputError(input) {
    // Remove error styling
    input.classList.remove('field__input--error');
    
    // Remove error message
    const errorElement = input.parentNode.querySelector('[data-imprint-error="true"]');
    if (errorElement) {
      errorElement.remove();
    }
  }

  // Form validation before submission
  validateForm(form) {
    const imprintSection = form.querySelector('.imprint-customization');
    if (!imprintSection) return true; // No imprint section, validation passes
    
    const sectionId = this.getSectionId(imprintSection);
    const select = imprintSection.querySelector(`#imprint-selection-${sectionId}`);
    const line1Input = imprintSection.querySelector(`#imprint-line-1-${sectionId}`);
    const line2Input = imprintSection.querySelector(`#imprint-line-2-${sectionId}`);
    
    if (!select) return true;
    
    // If "New Imprint: Text Only" is selected, validate text inputs
    if (select.value === 'New Imprint: Text Only') {
      let isValid = true;
      
      if (line1Input && line1Input.value.trim() === '') {
        this.showInputError(line1Input, 'Line 1 text is required');
        isValid = false;
      }
      
      if (line2Input && line2Input.value.trim() === '') {
        this.showInputError(line2Input, 'Line 2 text is required');
        isValid = false;
      }
      
      return isValid;
    }
    
    return true;
  }
}

// Initialize the imprint customization
const imprintCustomization = new ImprintCustomization();

// Integrate with product form submission (Dawn theme compatibility)
document.addEventListener('DOMContentLoaded', function() {
  // Find all product forms that might contain imprint customization
  const productForms = document.querySelectorAll('product-form form');
  
  productForms.forEach(form => {
    form.addEventListener('submit', function(event) {
      if (!imprintCustomization.validateForm(form)) {
        event.preventDefault();
        
        // Scroll to first error
        const firstError = form.querySelector('.field__input--error');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstError.focus();
        }
      }
    });
  });
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ImprintCustomization;
}